import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    ArrowLeft, Upload, PlayCircle, CheckCircle2, AlertTriangle,
    FileSpreadsheet, ArrowRight,
} from "lucide-react";

/**
 * /import/bulk — CSV upload for expenses, subscriptions, cards.
 * Two-phase workflow (preview → commit) with mandatory dry-run before
 * the first real write. Duplicates are surfaced in preview and can be
 * skipped or included on commit.
 */
const KIND_META = {
    expenses:      { label: "Expenses",      required: ["date", "merchant", "amount"] },
    subscriptions: { label: "Subscriptions", required: ["name", "amount"] },
    cards:         { label: "Cards",         required: ["bank_name", "last_4"] },
};

function Meter({ label, value, testid }) {
    return (
        <div data-testid={testid} className="flex-1 min-w-[110px] rounded-md border border-stone-200 bg-white px-3 py-2">
            <div className="text-[10.5px] uppercase tracking-widest text-stone-500">{label}</div>
            <div className="font-['Fraunces',serif] text-2xl text-[#0b1e3f]">{value}</div>
        </div>
    );
}

export default function BulkImport() {
    const nav = useNavigate();
    const [kind, setKind] = useState("expenses");
    const [csvText, setCsvText] = useState("");
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [committed, setCommitted] = useState(null);

    const runPreview = async () => {
        setBusy(true); setCommitted(null); setPreview(null);
        try {
            const r = await api.post(`/api/bulk-import/${kind}/preview`, { csv_text: csvText });
            setPreview(r.data);
            if (r.data.invalid_count > 0) {
                toast.warning(`${r.data.invalid_count} rows invalid — see preview`);
            } else if (r.data.total_rows === 0) {
                toast.error("No rows detected — check the header line");
            } else {
                toast.success(`Ready: ${r.data.valid_count} valid rows`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Preview failed");
        } finally { setBusy(false); }
    };

    const runCommit = async (dryRun) => {
        setBusy(true);
        try {
            const r = await api.post(`/api/bulk-import/${kind}/commit`,
                { csv_text: csvText, dry_run: dryRun, skip_duplicates: true });
            setCommitted(r.data);
            if (!dryRun) toast.success(`${r.data.inserted} imported`);
            else         toast.info(`Dry-run: ${r.data.would_insert} would be inserted`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Commit failed");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="bulk-import" className="min-h-screen bg-[#f6f3ec] text-[#0b1e3f]">
            <div className="max-w-4xl mx-auto p-6">
                <div className="flex items-center gap-3 mb-6">
                    <button onClick={() => nav(-1)} className="text-stone-500 hover:text-stone-800">
                        <ArrowLeft size={18} />
                    </button>
                    <div>
                        <div className="text-[10.5px] uppercase tracking-widest text-[#a0894a]">Bulk import</div>
                        <h1 className="font-['Fraunces',serif] text-2xl">Move existing records into VAULT</h1>
                    </div>
                </div>

                <Card className="mb-4">
                    <CardHeader>
                        <CardTitle className="text-base font-['Fraunces',serif]">Choose what you&apos;re importing</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap gap-2 mb-3">
                            {Object.entries(KIND_META).map(([k, meta]) => (
                                <button key={k} data-testid={`bulk-kind-${k}`}
                                    onClick={() => { setKind(k); setPreview(null); setCommitted(null); }}
                                    className={`px-3 py-1.5 text-sm rounded-full transition ${
                                        kind === k ? "bg-[#0b1e3f] text-[#f6f3ec]"
                                                   : "text-stone-700 hover:bg-stone-100 border border-stone-200"}`}>
                                    <FileSpreadsheet size={13} className="inline-block mr-1" /> {meta.label}
                                </button>
                            ))}
                        </div>
                        <div className="text-xs text-stone-500 mb-2">
                            Required columns for <b>{KIND_META[kind].label}</b>: {KIND_META[kind].required.join(", ")}.
                            {" "}Grab a starter file:
                            {" "}<Link className="underline underline-offset-2" to={`/api/import/templates/${kind}/download`}
                                target="_blank" rel="noreferrer">download {kind} template</Link>.
                        </div>
                        <Textarea data-testid="bulk-csv-textarea" rows={9} value={csvText}
                            onChange={e => setCsvText(e.target.value)}
                            placeholder="Paste CSV here — first row must be the header" />
                        <div className="flex gap-2 mt-3">
                            <Button data-testid="bulk-preview-btn" variant="outline"
                                onClick={runPreview} disabled={busy || !csvText}>
                                <Upload size={14} className="mr-1" /> Preview
                            </Button>
                            <Button data-testid="bulk-dryrun-btn" variant="outline"
                                onClick={() => runCommit(true)}
                                disabled={busy || !preview || preview.valid_count === 0}>
                                <PlayCircle size={14} className="mr-1" /> Dry-run
                            </Button>
                            <Button data-testid="bulk-commit-btn"
                                className="bg-[#0b1e3f] text-[#f6f3ec] hover:bg-[#0b1e3f]/90"
                                onClick={() => runCommit(false)}
                                disabled={busy || !preview || preview.valid_count === 0}>
                                <ArrowRight size={14} className="mr-1" /> Import for real
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {preview && (
                    <Card data-testid="bulk-preview-panel" className="mb-4">
                        <CardHeader>
                            <CardTitle className="text-base font-['Fraunces',serif]">Preview</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-wrap gap-2">
                                <Meter label="Rows"     value={preview.total_rows}     testid="bulk-meter-total" />
                                <Meter label="Valid"    value={preview.valid_count}    testid="bulk-meter-valid" />
                                <Meter label="Invalid"  value={preview.invalid_count}  testid="bulk-meter-invalid" />
                                <Meter label="Dupes (file)" value={preview.duplicates.length} testid="bulk-meter-dupes" />
                                <Meter label="Already in VAULT" value={preview.already_in_db} testid="bulk-meter-existing" />
                            </div>
                            {preview.invalid_count > 0 && (
                                <div className="mt-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
                                    <AlertTriangle size={14} className="mt-0.5" />
                                    <div>
                                        <b>{preview.invalid_count}</b> row(s) will be skipped.
                                        {" "}First few reasons: {preview.invalid.slice(0, 5).map(r => r.reason).join(", ")}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {committed && (
                    <Card data-testid="bulk-committed-panel">
                        <CardHeader>
                            <CardTitle className="text-base font-['Fraunces',serif]">
                                {committed.dry_run ? "Dry-run result" : "Import complete"}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-wrap gap-2">
                                <Meter label={committed.dry_run ? "Would insert" : "Inserted"}
                                    value={committed.dry_run ? committed.would_insert : committed.inserted}
                                    testid="bulk-result-inserted" />
                                <Meter label="Skipped dupes"    value={committed.skipped_duplicates} testid="bulk-result-dupes" />
                                <Meter label="Skipped invalid"  value={committed.skipped_invalid}    testid="bulk-result-invalid" />
                            </div>
                            {!committed.dry_run && (
                                <div className="mt-3 flex items-start gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-3">
                                    <CheckCircle2 size={14} className="mt-0.5" />
                                    <div>Batch <code className="text-xs">{committed.batch_id}</code> written.</div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}
